/**
 * Every number-to-string decision lives here.
 *
 * The contract ships raw numbers on purpose (`0.3342`, not `"33.42%"`), so
 * this module is the only place that knows a proportion is shown as a percent
 * and that -1e12 reads as "-1.00T". Nulls format as an em dash rather than 0:
 * "no value" and "zero" are different readings of the market.
 */

export const NO_VALUE = "—";

const MAGNITUDES: ReadonlyArray<readonly [number, string]> = [
	[1e12, "T"],
	[1e9, "B"],
	[1e6, "M"],
	[1e3, "K"],
];

/** Money in IDR, abbreviated the way the desk reads it: -1.00T, 812.40B. */
export function formatMoney(value: number | null | undefined, digits = 2): string {
	if (!Number.isFinite(value as number)) return NO_VALUE;
	const number = value as number;
	const magnitude = Math.abs(number);
	for (const [threshold, suffix] of MAGNITUDES) {
		if (magnitude >= threshold) return `${(number / threshold).toFixed(digits)}${suffix}`;
	}
	return number.toFixed(digits);
}

/** A 0..1 ratio (or a -1..1 correlation) as a percent. */
export function formatPercent(ratio: number | null | undefined, digits = 2): string {
	if (!Number.isFinite(ratio as number)) return NO_VALUE;
	return `${((ratio as number) * 100).toFixed(digits)}%`;
}

/** Any plain number, grouped and fixed to a width. */
export function formatNumber(value: number | null | undefined, digits = 0): string {
	if (!Number.isFinite(value as number)) return NO_VALUE;
	return (value as number).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** A price level. IDX quotes whole rupiah, so default to no decimals. */
export function formatPrice(value: number | null | undefined, digits = 0): string {
	return formatNumber(value, digits);
}

/**
 * The window a result covers, both ends.
 *
 * The header used to say only "as of <end>", which reads as a single day —
 * but a screener criterion is evaluated over a window, and how long that
 * window is changes what the answer means.
 */
export function formatDateRange(start: string | null | undefined, end: string | null | undefined): string | null {
	const from = formatDate(start);
	const to = formatDate(end);
	if (from === NO_VALUE && to === NO_VALUE) return null;
	if (from === NO_VALUE) return to;
	if (to === NO_VALUE) return from;
	return from === to ? from : `${from} – ${to}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/**
 * Strict ISO parse, deliberately not `new Date(...)`.
 *
 * A contract date is a calendar day with no timezone, and Intl month names
 * drift between runtimes ("Sep" vs "Sept" in en-GB), so the axis and the
 * header are built from the string itself.
 */
function parseIsoDate(iso: string): { year: number; month: number; day: number } | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
	if (!match) return null;
	const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
	if (month < 1 || month > 12 || day < 1 || day > 31) return null;
	return { year, month, day };
}

/** ISO date to the compact form the chart header uses: "18 Sep 2026". */
export function formatDate(iso: string | null | undefined): string {
	const parsed = iso ? parseIsoDate(iso) : null;
	if (!parsed) return NO_VALUE;
	return `${String(parsed.day).padStart(2, "0")} ${MONTHS[parsed.month - 1]} ${parsed.year}`;
}

/** "2026-08-01" as the holding-composition axis reads it: "Aug-26". */
export function formatMonth(iso: string | null | undefined): string {
	const parsed = iso ? parseIsoDate(iso) : null;
	if (!parsed) return NO_VALUE;
	return `${MONTHS[parsed.month - 1]}-${String(parsed.year).slice(-2)}`;
}

/** Signed change against a reference, e.g. close against VWAP. */
export function formatDelta(value: number | null | undefined, reference: number | null | undefined): string {
	if (!Number.isFinite(value as number) || !Number.isFinite(reference as number) || reference === 0) return NO_VALUE;
	const change = ((value as number) - (reference as number)) / Math.abs(reference as number);
	return `${change >= 0 ? "+" : ""}${(change * 100).toFixed(2)}%`;
}


/**
 * How strongly price tracked the flow, as a tone name.
 *
 * The bands are the ones the existing Quantist chart annotation uses — >=70%
 * strong, >=40% mixed, below that weak — so the summary header, the details
 * panel and the screener table all call the same reading the same colour.
 * Higher is better; null is not a weak correlation, it is no reading, and
 * gets no colour at all.
 */
export type CorrelationTone = "strong" | "mixed" | "weak" | "none";

export function correlationTone(ratio: number | null | undefined): CorrelationTone {
	if (!Number.isFinite(ratio as number)) return "none";
	const value = ratio as number;
	if (value >= 0.7) return "strong";
	if (value >= 0.4) return "mixed";
	return "weak";
}
